import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ConceptFamilyModel } from 'src/app/models/management/concept-family.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { FamilyTableModalModel } from 'src/app/models/tables/management/family-table-modal.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ConceptFamilyService } from 'src/app/services/management/concept-family.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-assign-families',
  templateUrl: './assign-families.component.html',
  styleUrls: ['./assign-families.component.scss'],
  standalone: false
})
export class AssignFamiliesComponent implements OnInit {

  @ViewChild('familiesModal') familiesModalComponent?: CustomModalComponent | null = null;

  private readonly conceptFamilyService = inject(ConceptFamilyService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendantFamily: boolean = true;
  columnSortFamily: string | null = null;
  conceptFamilies: ConceptFamilyModel[] = [];
  conceptInvoicingId: number = 0;
  dataFamily: PagedResponseModel<FamilyTableModalModel> = new PagedResponseModel<FamilyTableModalModel>();
  filtersFamily: ColumnResponseModel[] = [];
  headerFamily: ColumnResponseModel[] = [];
  inputsForFamiliesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  limitFamily: number = 10000;
  outputFamilies$ = new Subject<any>();
  pageFamily: number = 1;
  selectAll: boolean = false;
  selectedIdsToDelete: number[] = [];
  subscription: Subscription;
  subscriptionFamilies: Subscription;
  componentTableSelection = CustomTableSelectComponent;

  ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe(async (inputs: any) => {
      this.conceptInvoicingId = inputs.conceptInvoicingId;
      this.getConceptFamiliesByConceptInvoicingId();
    });
    this.subscriptionFamilies = this.outputFamilies$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeFamily(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.subscriptionFamilies?.unsubscribe();
  }

  async deleteSome() {
    const message = this.selectedIdsToDelete.length === 1
      ? '¿Realmente desea eliminar la familia seleccionada?'
      : `¿Realmente desea eliminar las ${this.selectedIdsToDelete.length} familias seleccionadas?`;

    const accepted = await this.confirmDialogService.confirm(message);
    if (!accepted) {
      return;
    }

    try {
      const idsToDelete: number[] = this.conceptFamilies
        .filter(x => x.conceptFamilyId && this.selectedIdsToDelete.includes(x.conceptFamilyId))
        .map(x => x.conceptFamilyId);

      await firstValueFrom(this.conceptFamilyService.deleteSomeAsync(idsToDelete));
      this.conceptFamilies = this.conceptFamilies.filter(x => !this.selectedIdsToDelete.includes(x.conceptFamilyId));
      this.selectedIdsToDelete = [];
      this.selectAll = false;
      this.getConceptFamiliesByConceptInvoicingId();
      this.toastHandlerService.showToast('success', 'Selección eliminada correctamente.');
    } catch (error) {
      console.error('Error al eliminar las familias seleccionadas.', error);
    }
  }

  async dispersedArticle(conceptFamily: ConceptFamilyModel, event: any) {
    try {
      conceptFamily.swDispersedArticle = event.checked;
      await firstValueFrom(this.conceptFamilyService.updateAsync(conceptFamily));
    } catch (error) {
      console.error('Error al actualizar la familia.', error);
    }
  }

  async getColumnsFamily() {
    try {
      if (this.headerFamily.length === 0) {
        this.headerFamily = await firstValueFrom(this.conceptFamilyService.getColumnsForFamiliesModalAsync());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para las familias.', error);
    }
  }

  async getConceptFamiliesByConceptInvoicingId() {
    try {
      this.conceptFamilies = await firstValueFrom(
        this.conceptFamilyService.getConceptFamiliesByConceptInvoicingIdAsync(this.conceptInvoicingId)
      );
      this.conceptFamilies.forEach(family => (family.selected = false));
      this.selectedIdsToDelete = [];
      this.selectAll = false;
    } catch (error) {
      console.error('Error al obtener las familias asignadas al concepto de facturación.', error);
    }
  }

  async getFamiliesForConceptInvoicing() {
    try {
      this.dataFamily = await firstValueFrom(
        this.conceptFamilyService.getFamiliesForConceptInvoicingAsync(
          this.conceptInvoicingId,
          this.pageFamily,
          this.limitFamily,
          this.columnSortFamily,
          this.ascendantFamily,
          this.filtersFamily
        )
      );
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem && selectedItem.length > 0) {
      const familiesIds = selectedItem.map((item: any) => item.familyId);
      await firstValueFrom(this.conceptFamilyService.createSomeAsync(this.conceptInvoicingId, familiesIds));
      this.getConceptFamiliesByConceptInvoicingId();
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersFamily = this.removeDuplicates(filters);
    this.filtersFamily.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChangeFamily(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.pageFamily = event.page;
    this.limitFamily = event.pageSize;
    this.columnSortFamily = event.sortField || null;
    this.ascendantFamily = event.sortOrder === 1;
    this.getFamiliesForConceptInvoicing();
  }

  async openFamiliesSelectionModal() {
    this.filtersFamily = [];
    await Promise.all([
      await this.getColumnsFamily(),
      this.getFamiliesForConceptInvoicing(),
    ]);
    this.updateInputs();

    if (this.familiesModalComponent && this.headerFamily) {
      this.familiesModalComponent.show();
    } else {
      this.toastHandlerService.showToast(
        'error',
        'No se pudieron cargar las familias. Por favor, inténtelo de nuevo.'
      );
    }
  }

  onRowSelectChange(conceptFamily: ConceptFamilyModel) {
    if (conceptFamily.selected) {
      if (!this.selectedIdsToDelete.includes(conceptFamily.conceptFamilyId)) {
        this.selectedIdsToDelete.push(conceptFamily.conceptFamilyId);
      }
    } else {
      this.selectedIdsToDelete = this.selectedIdsToDelete.filter(id => id !== conceptFamily.conceptFamilyId);
    }
    this.selectAll = this.conceptFamilies.length > 0 && this.selectedIdsToDelete.length === this.conceptFamilies.length;
  }

  onSelectAllChange(event: any) {
    const checked: boolean = event.checked;
    this.selectAll = checked;
    this.selectedIdsToDelete = [];

    this.conceptFamilies.forEach(family => {
      family.selected = checked;
      if (checked) {
        this.selectedIdsToDelete.push(family.conceptFamilyId);
      }
    });
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      this.filtersFamily = this.filtersFamily.filter(
        (f) => f.propertyName !== propertyName
      );
    } else {
      const existingFilter = this.filtersFamily.find(
        (f) => f.propertyName === propertyName
      );
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForFamiliesSelection$.next({
      headers: this.headerFamily,
      data: this.dataFamily,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersFamily,
    });
  }

}